import { action, computed, makeObservable, observable } from 'mobx'
import { Cubit } from './core'
import type { MeetingCubit } from './MeetingCubit'
import { createPausableTimer, type PausableTimer } from '../util/pausableTimer'

const NOTIFICATION_TIMEOUT = 10 * 1000 // 10 seconds

type SimpleModerationEvent = {
  slideId: string
  userId: string
}

export enum NotificationType {
  None = 0,
  ContributionOver = 1,
  ContributionUnder = 2,
}

export type NotificationState =
  | { type: NotificationType.None }
  | { type: NotificationType.ContributionOver; slideId: string }
  | { type: NotificationType.ContributionUnder; slideId: string }

export type NotificationDisplayState =
  | {
      type: NotificationType.None
    }
  | {
      type:
        | NotificationType.ContributionOver
        | NotificationType.ContributionUnder
      uniqueId: string
    }

export class MeetingSimpleModerationCubit extends Cubit {
  meetingCubit: MeetingCubit
  timer: PausableTimer | null = null

  @observable
  state: NotificationState = {
    type: NotificationType.None,
  }

  @observable
  uniqueId: string = generateUniqueId()

  constructor(meetingCubit: MeetingCubit) {
    super()
    this.meetingCubit = meetingCubit
    makeObservable(this)
  }

  async initialize() {
    const handleSimpleModerationContributionOver = (
      data: SimpleModerationEvent
    ) => {
      if (data.userId !== this.meetingCubit.currentUser.uid) {
        return
      }
      this.triggerContributionOver(data.slideId)
    }
    const handleSimpleModerationContributionUnder = (
      data: SimpleModerationEvent
    ) => {
      if (data.userId !== this.meetingCubit.currentUser.uid) {
        return
      }
      this.triggerContributionUnder(data.slideId)
    }

    this.meetingCubit.localCommands.on(
      'simpleModerationContributionOver',
      handleSimpleModerationContributionOver
    )
    this.meetingCubit.localCommands.on(
      'simpleModerationContributionUnder',
      handleSimpleModerationContributionUnder
    )
    this.disposers.push(() => {
      this.clearTimer()
      this.meetingCubit.localCommands.off(
        'simpleModerationContributionOver',
        handleSimpleModerationContributionOver
      )
      this.meetingCubit.localCommands.off(
        'simpleModerationContributionUnder',
        handleSimpleModerationContributionUnder
      )
    })
  }

  @action
  triggerContributionOver(slideId: string) {
    this.uniqueId = generateUniqueId()
    this.state = {
      type: NotificationType.ContributionOver,
      slideId,
    }
    this.startTimer()
    this.meetingCubit.logEvent('simple_moderation_contribution_over', {
      slideId,
    })
  }

  @action
  triggerContributionUnder(slideId: string) {
    this.uniqueId = generateUniqueId()
    this.state = { type: NotificationType.ContributionUnder, slideId }
    this.startTimer()
    this.meetingCubit.logEvent('simple_moderation_contribution_under', {
      slideId,
    })
  }

  @action
  userDismissed() {
    this.clearTimer()
    this.state = { type: NotificationType.None }
  }

  @action
  startTimer() {
    this.clearTimer()
    this.timer = createPausableTimer(
      action('timerOver', () => {
        this.state = { type: NotificationType.None }
      }),
      NOTIFICATION_TIMEOUT
    )
  }

  @action
  resumeTimer() {
    if (!this.timer) {
      return
    }
    this.timer.resume()
  }

  @action
  pauseTimer() {
    if (!this.timer) {
      return
    }
    this.timer.pause()
  }

  @computed
  get notificationType(): NotificationDisplayState {
    if (this.state.type === NotificationType.None) {
      return { type: NotificationType.None }
    }
    if (this.state.slideId !== this.meetingCubit.currentSlide?.id) {
      return { type: NotificationType.None }
    }
    return { type: this.state.type, uniqueId: this.uniqueId }
  }

  clearTimer() {
    if (this.timer) {
      this.timer.pause()
      this.timer = null
    }
  }
}

function generateUniqueId() {
  return Math.random().toString(36)
}
